import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, MessageCircle, MapPin, Clock, Mail, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useSite } from "@/context/SiteContext";
import { api } from "@/lib/api";
import Chapter from "./Chapter";

const inputCls =
  "w-full rounded-lg border border-navy-border bg-navy px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:border-volt focus:outline-none";

export default function Contact() {
  const { content, t, ui } = useSite();
  const wa = `https://wa.me/${content.whatsapp}?text=${encodeURIComponent(ui.whatsappMsg)}`;
  const [form, setForm] = useState({ name: "", phone: "", email: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const submitLead = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/leads", form);
      toast.success(ui.formSuccess);
      setForm({ name: "", phone: "", email: "", service: "", message: "" });
    } catch (err) {
      toast.error(ui.formError);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="mx-auto max-w-7xl scroll-mt-28 px-6 py-24" data-testid="contact-section">
      <Chapter num="04" title={ui.contactTitle} sub={ui.contactSub} testId="contact-chapter" />

      <div className="grid gap-4 md:grid-cols-2">
        <motion.a
          data-testid="contact-phone-cta"
          href={`tel:${content.phone_href}`}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55 }}
          className="group rounded-2xl bg-volt p-8 transition-all hover:-translate-y-1 hover:shadow-[0_16px_50px_rgba(255,212,0,0.3)]"
        >
          <div className="flex items-center gap-3 text-navy/70">
            <Phone size={20} />
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em]">{ui.call}</span>
          </div>
          <p className="mt-4 font-display text-4xl tracking-wide text-navy sm:text-5xl">{content.phone}</p>
          <p className="mt-2 text-sm font-semibold text-navy/70">{ui.freeQuote}</p>
        </motion.a>

        <motion.a
          data-testid="contact-whatsapp-cta"
          href={wa}
          target="_blank"
          rel="noopener noreferrer"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.55, delay: 0.1 }}
          className="group rounded-2xl bg-[#25D366] p-8 transition-all hover:-translate-y-1 hover:shadow-[0_16px_50px_rgba(37,211,102,0.3)]"
        >
          <div className="flex items-center gap-3 text-navy/70">
            <MessageCircle size={20} />
            <span className="font-mono text-xs font-semibold uppercase tracking-[0.25em]">WhatsApp</span>
          </div>
          <p className="mt-4 font-display text-4xl uppercase tracking-wide text-navy sm:text-5xl">{ui.whatsapp}</p>
          <p className="mt-2 text-sm font-semibold text-navy/70">{content.phone}</p>
        </motion.a>
      </div>

      <div className="mt-8 grid gap-4 rounded-2xl border border-navy-border bg-navy-surface p-8 sm:grid-cols-3">
        <div data-testid="contact-location">
          <div className="flex items-center gap-2 text-volt"><MapPin size={16} /><span className="font-mono text-xs uppercase tracking-widest">{ui.location}</span></div>
          <p className="mt-2 text-sm text-slate-300">{t(content.contact.location)}</p>
        </div>
        <div data-testid="contact-hours">
          <div className="flex items-center gap-2 text-volt"><Clock size={16} /><span className="font-mono text-xs uppercase tracking-widest">{ui.hours}</span></div>
          <p className="mt-2 text-sm text-slate-300">{t(content.contact.hours)}</p>
        </div>
        <div data-testid="contact-email">
          <div className="flex items-center gap-2 text-volt"><Mail size={16} /><span className="font-mono text-xs uppercase tracking-widest">Email</span></div>
          <a href={`mailto:${content.email}`} className="mt-2 block break-all text-sm text-slate-300 hover:text-volt">{content.email}</a>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.55 }}
        className="mt-8 rounded-2xl border border-volt/40 bg-navy-surface p-8"
      >
        <h3 className="font-display text-3xl uppercase tracking-wide text-white">{ui.formTitle}</h3>
        <form onSubmit={submitLead} className="mt-6 grid gap-4 sm:grid-cols-2" data-testid="quote-form">
          <input data-testid="quote-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={ui.formName} className={inputCls} />
          <input data-testid="quote-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={ui.formPhone} className={inputCls} />
          <input data-testid="quote-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder={ui.formEmail} className={inputCls} />
          <select data-testid="quote-service" value={form.service} onChange={(e) => setForm({ ...form, service: e.target.value })} className={inputCls}>
            <option value="">{ui.formService}</option>
            {content.services.map((s) => (
              <option key={s.id} value={t(s.title)}>{t(s.title)}</option>
            ))}
            <option value="Otro / Other">{ui.formOther}</option>
          </select>
          <textarea data-testid="quote-message" required rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder={ui.formMessage} className={`${inputCls} sm:col-span-2`} />
          <button
            data-testid="quote-submit"
            type="submit"
            disabled={sending}
            className="flex items-center justify-center gap-2 rounded-full bg-volt px-7 py-4 text-sm font-extrabold uppercase tracking-wide text-navy transition-all hover:-translate-y-0.5 hover:bg-volt-hover disabled:opacity-60 sm:col-span-2"
          >
            {sending ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
            {sending ? ui.formSending : ui.formSend}
          </button>
        </form>
      </motion.div>

      <p className="mt-8 text-center text-sm leading-relaxed text-slate-500" data-testid="contact-cities">
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-volt">{ui.cities}</span>
        <br />
        {t(content.contact.cities)}
      </p>
    </section>
  );
}
